import { ITrafficSource } from '@clickster/constant-definitions/dist-esm/entities/traffic-source/types';
import { IQueryProps } from '../interfaces/general/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import orderBy from 'lodash/orderBy';
import { useMemo } from 'react';
import { TrafficService, type IGoogleConversionAction } from '../services/traffic/traffic-rest';

interface IUseGetAffiliatesNetwork extends IQueryProps<ITrafficSource> {}

export const useGetTrafficSource = ({
  params,
  handleSuccess,
  handleError,
  staleTime = 0,
  customKey,
  enabled
}: IUseGetAffiliatesNetwork) => {
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery(customKey ? ['trafficSources', customKey] : ['trafficSources'], () => TrafficService.list(params), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: staleTime,
    enabled
  });

  const trafficSources = useMemo(() => orderBy(data ?? [], ['name'], ['asc']) as ITrafficSource[], [data]);

  const actives = useMemo(() => trafficSources.filter((ts) => ts.status === 'active'), [trafficSources]);

  const byApiName = useMemo(() => {
    return actives.map((ts) => {
      return ts.api?.name!;
    });
  }, [actives]);

  const byIdName = useMemo(() => {
    return trafficSources.map((ts) => {
      return {
        id: ts._id,
        name: ts.name,
        status: ts.status,
      };
    });
  }, [trafficSources]);

  return {
    trafficSources,
    trafficSourcesActives: actives,
    trafficSourcesByApiName: byApiName,
    trafficSourcesByIdName: byIdName,
    isLoading,
    isFetching,
    refetch,
  };
};

interface IUseGetAffiliateById extends IQueryProps<ITrafficSource> {
  id: string;
}
export const useGetTrafficSourceById = ({ id, enabled, params, handleSuccess, handleError }: IUseGetAffiliateById) => {
  const {
    data = {},
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['trafficSourceById', id], () => TrafficService.find(id, { ...params }), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    enabled: enabled && !!id,
  });

  return {
    traffic: (data ?? {}) as ITrafficSource,
    isLoading,
    isFetching,
    refetch,
  };
};

export const useCreateTrafficSource = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    (traffic: ITrafficSource) => TrafficService.create(traffic),
    {
      onError: (error) => console.log({ error }),
    },
  );

  return {
    createTrafficSource: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useUpdateTrafficSource = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    (traffic: ITrafficSource) => TrafficService.update(traffic),
    {
      onError: (error) => console.log({ error }),
    },
  );
  return {
    updateTrafficSource: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useUpdateCpcDateRange = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    ({
      trafficSourceId,
      dates,
    }: {
      trafficSourceId: string;
      dates: { from: string | Date; to: string | Date; time_offset: number };
    }) => TrafficService.cpcDateRange(trafficSourceId, dates),
    {
      onError: (error) => console.log({ error }),
    },
  );

  return {
    updateCpcDateRange: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

interface IUseGetHelper extends IQueryProps<any[]> {
  helper: string;
  trafficSourceId: string;
  country?: string;
  accountId?: string;
}

export const usePostHelper = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    (data: any) => TrafficService.postHelper(data.helper, data.trafficSourceId, data.data),
    {
      onError: (error) => console.log({ error }),
    },
  );
  return {
    postHelper: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useGetHelper = ({
  helper,
  trafficSourceId,
  country,
  accountId,
  enabled = true,
  handleSuccess,
  handleError,
  staleTime = undefined,
  params,
}: IUseGetHelper) => {
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ['trafficSourceHelper', helper, trafficSourceId, JSON.stringify(params)],
    () => TrafficService.getHelper(helper, trafficSourceId, country, accountId!, params),
    {
      onError: handleError
        ? handleError
        : (error) => {
            console.log({ error });
          },
      onSuccess: handleSuccess ? handleSuccess : undefined,
      enabled: enabled && !!helper && !!trafficSourceId,
      staleTime: staleTime,
    },
  );

  return {
    helperData: data,
    isLoading,
    isFetching,
    refetch,
  };
};

interface IUseGetConversionEvents {
  trafficSourceId: string;
  accountId: string;
  enabled?: boolean;
  handleSuccess?: (data: any) => void;
  handleError?: (error: unknown) => void;
}

export const useGetConversionEvents = ({
  trafficSourceId,
  accountId,
  enabled = true,
  handleSuccess,
  handleError,
}: IUseGetConversionEvents) => {
  const { data, isLoading, isFetching, refetch } = useQuery(
    ['conversionEvents', trafficSourceId, accountId],
    () => TrafficService.getConversionEvents(trafficSourceId, accountId),
    {
      enabled,
      onSuccess: handleSuccess,
      onError: handleError
        ? handleError
        : (error) => {
            console.log({ error });
          },
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  return {
    conversionEvents: data,
    isLoading,
    isFetching,
    refetch,
  };
};

export const useMutateHelper = () => {
  const { mutateAsync, isLoading, isError, isSuccess } = useMutation(
    ({ helper, trafficSourceId, country }: { helper: string; trafficSourceId: string; country?: string }) =>
      TrafficService.getHelper(helper, trafficSourceId, country),
    {
      onError: (error) => {},
    },
  );

  return {
    mutateHelper: mutateAsync,
    isLoading,
    isError,
    isSuccess,
  };
};

export const useMutateConsecutiveNumber = () => {
  const { mutateAsync, isLoading, isError, isSuccess } = useMutation(
    ({ tsId }: { tsId: string }) => TrafficService.getNextConsecutiveCampaignNumber(tsId),
    {
      onError: (error) => {},
    },
  );

  return {
    mutateConsecutiveNumber: mutateAsync,
    isLoading,
    isError,
    isSuccess,
  };
};

interface IUseGetCallToActions extends IQueryProps<string[]>{
  trafficSourceId: string;
}

export const useGetCallToActions = ({
  trafficSourceId,
  enabled = false,
  handleSuccess,
  handleError,
}: IUseGetCallToActions) => {
  const { data, isLoading, isFetching, refetch } = useQuery(
    ['useGetCallToActions', trafficSourceId],
    () => TrafficService.getCallToActionOptions(trafficSourceId),
    {
      onSuccess: (data) => {
        if (handleSuccess) {
          handleSuccess(data);
        }
      },
      onError: handleError
        ? handleError
        : (error) => {
            console.log({ error });
          },
      retry: false,
      refetchOnWindowFocus: false,
      enabled,
    },
  );

  return {
    callToActions: data as string[],
    isLoading,
    isFetching,
    refetch,
  };
};

interface IUseGetGoogleConversionActions {
  trafficSourceId: string;
  enabled?: boolean;
  handleSuccess?: (data: IGoogleConversionAction[]) => void;
  handleError?: (error: unknown) => void;
}

/**
 * React Query hook that fetches Google Ads conversion actions for a traffic source.
 *
 * @param trafficSourceId - The traffic source `_id`.
 * @param enabled - Whether to run the query (defaults to `true`).
 */
export const useGetGoogleConversionActions = ({
  trafficSourceId,
  enabled = true,
  handleSuccess,
  handleError,
}: IUseGetGoogleConversionActions) => {
  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    ['googleConversionActions', trafficSourceId],
    () => TrafficService.getGoogleConversionActions(trafficSourceId),
    {
      enabled: enabled && !!trafficSourceId,
      onSuccess: handleSuccess,
      onError: handleError
        ? handleError
        : (error) => {
            console.log({ error });
          },
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  return {
    conversionActions: (data ?? []) as IGoogleConversionAction[],
    isLoading,
    isFetching,
    isError,
    refetch,
  };
};

export type { IGoogleConversionAction };

export const useMutateGetTrafficSourceById = (
  handleSuccess?: (data: ITrafficSource) => void,
  handleError?: (error: any) => void,
) => {
  const { mutateAsync, isLoading, isError, data } = useMutation((id: string) => TrafficService.find(id, {}), {
    onSuccess: (data) => {
      if (handleSuccess) handleSuccess(data);
    },
    onError: (error) => {
      console.log({ error });
      if (handleError) handleError(error);
    },
  });

  return {
    getTrafficSourceById: mutateAsync,
    isLoading,
    isError,
    trafficSource: data,
  };
};
